import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Download, Loader2, Footprints, Car, ArrowUp, ExternalLink } from 'lucide-react';
import { roadsMeta, covers, areaOf, loadRoads, routeOff, projPts } from '../lib/offRoute';
import { buildNav, navState, stepText } from '../lib/navi';
import { useHeading } from '../lib/useHeading';
import { geoDist } from '../lib/geo';
import { routeLink } from '../lib/route';
import { speak } from '../lib/a11y';

export const openNavi = (tgt) => window.dispatchEvent(new CustomEvent('giss-onav-open', { detail: tgt }));
const brg = (p, q) => { const r = Math.PI / 180, y = Math.sin((q[1] - p[1]) * r) * Math.cos(q[0] * r), x = Math.cos(p[0] * r) * Math.sin(q[0] * r) - Math.sin(p[0] * r) * Math.cos(q[0] * r) * Math.cos((q[1] - p[1]) * r); return (Math.atan2(y, x) / r + 360) % 360; };
const fmt = (m) => (m == null ? '–' : m >= 1000 ? `${(m / 1000).toFixed(1).replace('.', ',')} km` : `${Math.round(m)} m`);
const drawRoute = (pts, name) => window.dispatchEvent(new CustomEvent('giss-agent-route', { detail: { pts: pts || [], name, keep: true, noFit: !pts } }));

function usePos(on) {
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!on || !navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition((x) => setP({ lat: x.coords.latitude, lon: x.coords.longitude, acc: x.coords.accuracy, t: Date.now() }), () => {}, { enableHighAccuracy: true, maximumAge: 3000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [on]);
  return p;
}

function RoadState({ t, meta, busy, onLoad, ok }) {
  return (
    <div className="onav-roads" data-testid="onav-roads">
      {meta ? <small data-testid="onav-roads-info">{ok ? '✓' : '!'} {t.onRoads.replace('{km}', meta.km).replace('{d}', new Date(meta.at).toLocaleDateString(t.locale))}{ok ? '' : ` · ${t.onOutside}`}</small> : <small data-testid="onav-roads-none">{t.onNoRoads}</small>}
      <button className="btn btn-outline btn-sm" data-testid="onav-load-roads" disabled={busy || !navigator.onLine} onClick={onLoad}>{busy ? <Loader2 size={14} className="spin" /> : <Download size={14} />} {meta ? t.onReload : t.onLoad}</button>
    </div>
  );
}

// offline navigation to an object: arrow + straight distance (works everywhere), street route from the stored road network
export default function ObjNavi({ t, project }) {
  const [tgt, setTgt] = useState(null), [mode, setMode] = useState(() => localStorage.getItem('giss_onav_mode') || 'foot');
  const [meta, setMeta] = useState(null), [busy, setBusy] = useState(false), [nav, setNav] = useState(null), [st, setSt] = useState(null);
  const pos = usePos(!!tgt), hc = useHeading('compass', pos), hw = useHeading('walk', pos), h = hc.h ?? hw.h;
  const m = useRef({ said: {}, idx: 0, offN: 0 });
  useEffect(() => { const on = (e) => setTgt(e.detail); window.addEventListener('giss-onav-open', on); return () => window.removeEventListener('giss-onav-open', on); }, []);
  useEffect(() => { if (tgt) roadsMeta(project.id).then(setMeta); }, [tgt]); // eslint-disable-line react-hooks/exhaustive-deps
  const here = pos ? [pos.lat, pos.lon] : null, goal = tgt ? [tgt.lat, tgt.lon] : null;
  const ok = !!meta && !!goal && covers(meta, here ? [here, goal] : [goal]);
  const load = async (quiet) => {
    setBusy(true);
    const pts = [...projPts(project), goal, here].filter(Boolean);
    try { setMeta(await loadRoads(project.id, areaOf(pts))); toast.success(t.onLoaded); } catch (e) { if (quiet !== true) toast.error(`${t.onLoadErr} (${e.message})`); }
    setBusy(false);
  };
  // auto-load once when online and the area is not stored yet
  useEffect(() => { if (tgt && meta === null && navigator.onLine) roadsMeta(project.id).then((x) => { if (!x) load(true); }); }, [tgt]); // eslint-disable-line react-hooks/exhaustive-deps
  const calc = async (from) => {
    const r = ok && from ? await routeOff(project.id, from, goal, mode) : null;
    m.current = { said: {}, idx: 0, offN: 0, re: m.current.re };
    if (!r) { setNav(null); drawRoute(from ? [from, goal] : null, tgt?.name); return; }
    setNav(buildNav(r.pts, r.steps)); drawRoute(r.pts, tgt.name);
  };
  useEffect(() => { if (tgt && here) calc(here); }, [tgt, mode, ok, !!here]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!nav || !pos) return;
    const s = navState(nav, pos, m.current.idx), mm = m.current;
    mm.idx = s.idx; setSt(s);
    if (s.next) { const k = `${s.next.at}`; for (const lim of [100, 25]) if (s.dist <= lim && !mm.said[k + lim]) { mm.said[k + lim] = 1; speak(stepText(t, s.next, s.dist)); } }
    mm.offN = s.off > 40 ? mm.offN + 1 : 0;
    if ((mm.offN >= 3 || s.off > 150) && !(mm.re > Date.now())) { mm.offN = 0; mm.re = Date.now() + 5000; speak(t.nvReroute); calc(here); }
  }, [pos?.t, nav]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!tgt) return null;
  const close = () => { setTgt(null); setNav(null); setSt(null); drawRoute(null); };
  const d = here ? geoDist(here, goal) : null, b = here ? brg(here, goal) : 0, rot = h != null ? b - h : b;
  const arrived = d != null && d < Math.max(8, (pos?.acc || 0) * 0.8);
  return (
    <div className="onav" data-testid="onav-panel">
      <div className="onav-head"><b data-testid="onav-title">🧭 {tgt.name || t.onTitle}</b><button className="icon-btn" data-testid="onav-close" aria-label="x" onClick={close}><X size={16} /></button></div>
      <div className="onav-main">
        <span className="onav-arrow" data-testid="onav-arrow" style={{ transform: `rotate(${rot}deg)` }}><ArrowUp size={54} strokeWidth={2.8} /></span>
        <div className="onav-txt">
          <b data-testid="onav-dist">{arrived ? t.onArrived : `${fmt(d)} ${t.onAir}`}</b>
          <small data-testid="onav-ref">{h != null ? t.onRefDev : `${t.onRefNorth} · ${Math.round(b)}°`}{pos?.acc ? ` · GPS ±${Math.round(pos.acc)} m` : ''}</small>
          {!here && <small data-testid="onav-wait">{t.nvWait}</small>}
          {nav && st && !arrived && <small data-testid="onav-route">{t.onRoute}: {fmt(st.left)} · {st.next ? stepText(t, st.next, st.dist) : t.nv_arrive}</small>}
          {here && !nav && <small data-testid="onav-noroute">{ok ? t.onNoPath : t.onAirOnly}</small>}
        </div>
      </div>
      <div className="row">
        <button className={`chip seg ${mode === 'foot' ? 'on' : ''}`} data-testid="onav-mode-foot" onClick={() => { setMode('foot'); localStorage.setItem('giss_onav_mode', 'foot'); }}><Footprints size={14} /> {t.onFoot}</button>
        <button className={`chip seg ${mode === 'car' ? 'on' : ''}`} data-testid="onav-mode-car" onClick={() => { setMode('car'); localStorage.setItem('giss_onav_mode', 'car'); }}><Car size={14} /> {t.onCar}</button>
        <a className="btn btn-outline btn-sm" data-testid="onav-google" href={routeLink({ lat: tgt.lat, lon: tgt.lon }).replace('travelmode=driving', `travelmode=${mode === 'car' ? 'driving' : 'walking'}`)} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Google Maps</a>
      </div>
      <RoadState t={t} meta={meta} busy={busy} onLoad={load} ok={ok} />
    </div>
  );
}
